import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import useFlightDetailsFromSearchResults from "../../hooks/useFlightDetailsFromSearchResults.js";
import { getFlight, getFlightDetailsFetchStatus } from "../../store/flightDetails/selectors.js";
import { isOfGoogleFlightsOrigin, isOfSkyScannerOrigin } from "../../utils/marketing-url-params.js";
import { actions as flightDetailsActions } from "../../store/flightDetails/actions.js";
import { actions as extraProductActions } from "../../ancillaries/store/extraProducts/actions.js";
import { trackMetaLandingsCustomGoalsV2 } from "../../utils/experiments/track-meta-landings-v2.js";
import { metaPreOptedAncillariesFromFlightToStore } from "../../utils/preOptedAncillaries.js";
import { carrierUtils, flightsSessionStore, pricingUtils } from "@flights/web-api-utils-universal";
import { FLIGHT_DETAILS_MODAL_CLOSE_ERROR_CODES, PRICE_ACCURACY_TAG_FIRED_OFFER } from "../../constants.js";
import { useGoogleFlightsPriceAccuracyTag, useSkyScannerPriceAccuracyTag } from "../../hooks/useGTMPriceAccuracyTags.js";
import useEventTracking from "../../hooks/useEventTracking.js";
import useRetryFlightDetailsApi from "../../hooks/useRetryFlightDetailsApi.js";
import useCreateUrl from "../../hooks/useCreateUrl.js";
import useEvents from "../../hooks/useEvents.js";
import useGlobalContext from "../../hooks/useGlobalContext.js";
import useClientMetrics from "../../hooks/useClientMetrics.js";
import { usePrefetchedBrandedFaresOffer, useSearchResultOffer } from "../../hooks/useSearchResults.js";
import useBrandedFares from "../../branded-fares/hooks/useBrandedFares.js";
import useReportSRFDMismatch from "../../hooks/useReportSRFDMismatch.js";
import useGetSearchResultsBound from "../../hooks/useGetSearchResultsBound.js";
import { useSplitBoundsCondition } from "../../utils/experiments/funnel/flights_split_bounds_us.js";
export function useFlightDetailsMobileController(params, landingOfferFromMeta) {
    const history = useHistory();
    const dispatch = useDispatch();
    const { grumble } = useEvents();
    const { createUrl } = useCreateUrl();
    const flightFD = useSelector(getFlight);
    const { trackRenderTime, trackClientMetric } = useClientMetrics();
    const isSkyscannerUser = isOfSkyScannerOrigin();
    const { features, requestId } = useGlobalContext();
    const flightOffer = useSearchResultOffer(params?.id);
    const brandedFareOffer = useBrandedFares(params?.id);
    const isGoogleFlightsUser = isOfGoogleFlightsOrigin();
    const fetchStatus = useSelector(getFlightDetailsFetchStatus);
    const trackV2 = useEventTracking("flight_details", requestId);
    const flightSR = useFlightDetailsFromSearchResults(params?.id);
    const skyScannerPriceAccuracyTag = useSkyScannerPriceAccuracyTag();
    const { setFlightDetailsApiForRetry } = useRetryFlightDetailsApi();
    const googleFlightsPriceAccuracyTag = useGoogleFlightsPriceAccuracyTag();
    const prefetchedbrandedFareOffer = usePrefetchedBrandedFaresOffer(params?.id);
    useReportSRFDMismatch(params?.id);
    const bound = useGetSearchResultsBound();
    const { isSplitBounds } = useSplitBoundsCondition();
    const flight = useMemo(() => {
        if (fetchStatus === "success" && flightFD)
            return flightFD;
        return flightSR;
    }, [flightSR, fetchStatus, flightFD]);
    const enableGfsPosMismatch = useMemo(() => {
        return features["FLIGHTS_META_ENABLE_GFS_FOR_POS_MISMATCH"] && isGoogleFlightsUser;
    }, [features, isGoogleFlightsUser]);
    const posMismatch = useMemo(() => {
        return flight?.posMismatch?.isPOSMismatch && !enableGfsPosMismatch ? flight.posMismatch : undefined;
    }, [flight, enableGfsPosMismatch]);
    const hasPrefetchedBrandedFareOffers = useMemo(() => {
        return !!((flightOffer &&
            !flightOffer.requestableBrandedFares &&
            !prefetchedbrandedFareOffer &&
            brandedFareOffer?.brandedFareOffers?.length) ||
            prefetchedbrandedFareOffer);
    }, [flightOffer, prefetchedbrandedFareOffer, brandedFareOffer]);
    const backToSearch = useCallback((p) => {
        dispatch(flightDetailsActions.fetch());
        const urlParams = new URLSearchParams(location.search);
        urlParams.delete("selectedProducts");
        urlParams.delete("brandedFareIndex");
        trackMetaLandingsCustomGoalsV2(1, "mdot");
        let url = `/flights/${params?.airports}?${urlParams.toString()}`;
        if (isSplitBounds) {
            if (bound === "outbound") {
                url = `flights/${params?.airports}/outbound/?${urlParams.toString()}`;
            }
            if (bound === "inbound") {
                url = `flights/${params?.airports}/inbound/${params?.outboundToken}/?${urlParams.toString()}`;
            }
        }
        history.push(createUrl(url), { forceRefetch: p?.forceReload });
    }, [dispatch, history, createUrl, params?.airports, params?.outboundToken, isSplitBounds, bound]);
    const onFetchSuccess = useCallback((data) => {
        dispatch(flightDetailsActions.fetchSuccess(data));
        if (data.ancillaries)
            metaPreOptedAncillariesFromFlightToStore(data.ancillaries, extraProductActions);
        const totalPrice = pricingUtils.priceToNumber(data.priceBreakdown.total);
        if (params?.id === landingOfferFromMeta && isGoogleFlightsUser) {
            trackV2("google_flights_landing_shallow", {
                isShallow: false,
                airlineIATA: carrierUtils.getUniqueCarrierCodes(data.segments)
            });
            trackClientMetric("google_flights_price_accuracy_tag_fired");
            googleFlightsPriceAccuracyTag(totalPrice, data.priceBreakdown.total.currencyCode);
        }
        if (params?.id === landingOfferFromMeta &&
            flightsSessionStore.get(PRICE_ACCURACY_TAG_FIRED_OFFER) !== params?.id &&
            isSkyscannerUser) {
            skyScannerPriceAccuracyTag(totalPrice, data.priceBreakdown.total.currencyCode);
            flightsSessionStore.set(PRICE_ACCURACY_TAG_FIRED_OFFER, params?.id);
        }
    }, [
        dispatch,
        params?.id,
        landingOfferFromMeta,
        isGoogleFlightsUser,
        isSkyscannerUser,
        trackV2,
        trackClientMetric,
        googleFlightsPriceAccuracyTag,
        skyScannerPriceAccuracyTag
    ]);
    const onFetch = useCallback(() => {
        dispatch(flightDetailsActions.fetch());
    }, [dispatch]);
    const onFetchError = useCallback((error) => {
        grumble("Failed to fetch flight details on mobile", error);
        if (FLIGHT_DETAILS_MODAL_CLOSE_ERROR_CODES.includes(error.code)) {
            backToSearch({ forceReload: false });
            return;
        }
        if (isGoogleFlightsUser) {
            trackV2("google_flights_landing_shallow", {
                isShallow: true,
                airlineIATA: []
            });
            trackClientMetric("google_flights_price_accuracy_tag_fired");
            trackClientMetric("google_flights_price_accuracy_tag_shallow");
            googleFlightsPriceAccuracyTag(0, "");
        }
        if (flight)
            setFlightDetailsApiForRetry(error);
        dispatch(flightDetailsActions.fetchError(error));
    }, [
        flight,
        trackV2,
        grumble,
        dispatch,
        backToSearch,
        isGoogleFlightsUser,
        trackClientMetric,
        setFlightDetailsApiForRetry,
        googleFlightsPriceAccuracyTag
    ]);
    const onFetchAbort = useCallback(() => {
        dispatch(flightDetailsActions.fetchReset());
    }, [dispatch]);
    return {
        flight,
        onFetch,
        onFetchAbort,
        posMismatch,
        onFetchError,
        backToSearch,
        onFetchSuccess,
        trackRenderTime,
        hasPrefetchedBrandedFareOffers
    };
}
